import "./Notifications.scss";
import { Component, ComponentProps, useState } from "react";
import { useIntl } from "react-intl";
import { Alert } from "@metro-ui/core";
import type { AlertVariant } from "@metro-ui/core/es6/Alert/utils";
import type { Notification } from "../uidispatcherapi/NotificationStore";
import NotificationStore from "../uidispatcherapi/NotificationStore";
import { withReactAdapter } from "../lazy-loading/withReactAdapter";

const styleByNotificationType: { [key: string]: AlertVariant } = {
	"server-error": "error",
	"user-error": "warning",
	warning: "warning",
	info: "info",
	success: "success",
};

const DEFAULT_NOTIFICATIONS_TIMEOUT_MS = 10000;
function InfoBox({ notification }: { notification: Notification }) {
	const [isDismissed, setIsDismissed] = useState(false);
	const intl = useIntl();
	const bsStyle = styleByNotificationType[notification.type] ?? "info";

	const dismiss = () => {
		setIsDismissed(true);
		setTimeout(() => {
			NotificationStore.dismiss(notification);
		}, 1000);
	};

	setTimeout(dismiss, notification.timeout ?? DEFAULT_NOTIFICATIONS_TIMEOUT_MS);

	const dismissableProps: Partial<ComponentProps<typeof Alert>> = {};

	if (notification.dismissable ?? !notification.timeout) {
		dismissableProps.ctaText = "";
		dismissableProps.onCtaClick = dismiss;
		dismissableProps.closeIconLabel = intl.formatMessage({ id: "PL_CLOSE_NOTIFICATION" });
	}

	return (
		<Alert
			className={"mfcss-notification" + (isDismissed ? " unmount" : "")}
			variant={bsStyle}
			size="medium"
			title={notification.text}
			{...dismissableProps}></Alert>
	);
}

export class Notifications extends Component<
	Record<PropertyKey, never>,
	{
		notifications: ReadonlyArray<Notification>;
	}
> {
	_unsubscribe: () => void;

	constructor(props) {
		super(props);
		this.state = this._getStateFromStore();
		this._unsubscribe = NotificationStore.listen(this.handleNotificationUpdate.bind(this));
	}

	componentWillUnmount() {
		if (typeof this._unsubscribe === "function") {
			this._unsubscribe();
		}
	}

	handleNotificationUpdate: (...args: Array<any>) => any = () => {
		this.setState(this._getStateFromStore());
	};

	_getStateFromStore() {
		return {
			notifications: NotificationStore.get() || [],
		};
	}

	render() {
		return this.state.notifications.length ? (
			<div className="mfcss-notification-wrapper">
				{this.state.notifications.map(notification => (
					<InfoBox notification={notification} key={notification.uuid} />
				))}
			</div>
		) : null;
	}
}

// There is a problem with react-intl context inheritance when using different react-intl versions in component tree.
// Since Notification API can be called from any sub-ui it's safer to render Notifications in Adapter.
export const NotificationsWithReactAdapter = withReactAdapter(Notifications);
